/**
 * useAgentSession: "who am I talking to" in a team conversation.
 *
 * Starting a conversation with a shared agent (Use / Ask owner / a chip / an @mention) shows a pill above the message box. While it is there,
 * messages without an @mention go to that agent, so a reply to its question does not need an @ and does not fall to the whole team. It ends
 * with ✕, after 30 idle minutes, or when the agent stops being shared. It is yours alone: nobody else sees it, and it never hides: the pill
 * is always on screen while it applies. (An earlier version kept invisible "attached" agents in the browser, which silently steered messages.)
 */
import { useCallback, useEffect, useState } from 'react';

export const SESSION_TTL_MS = 30 * 60 * 1000;
const key = (spaceId) => `lyra:talk-to:${spaceId}`;

const store = () => { try { return window.sessionStorage; } catch { return null; } };

/** The stored session, or null when there is none or it has gone idle. */
export function readSession(spaceId, now = Date.now()) {
  const s = store(); if (!s) return null;
  try {
    const v = JSON.parse(s.getItem(key(spaceId)) || 'null');
    if (!v || !v.agentId) return null;
    if (now - Number(v.at || 0) > SESSION_TTL_MS) { s.removeItem(key(spaceId)); return null; }
    return v;
  } catch { return null; }
}
const write = (spaceId, v) => { const s = store(); try { if (!s) return; if (v) s.setItem(key(spaceId), JSON.stringify(v)); else s.removeItem(key(spaceId)); } catch { /* not remembered */ } };

/** Does the text contain an @mention of anything? A typed mention always wins over the conversation: it is how you ask someone else, once. */
export const hasTypedMention = (text) => /(^|\s)@[\w'’]/.test(String(text || ''));

/** The shared agent @mentioned by name in the text, if any (longest name first, so "Budget Planner Pro" beats "Budget Planner"). */
export function typedAgentIn(text, agents = []) {
  const t = String(text || '').toLowerCase();
  const sorted = [...agents].sort((a, b) => String(b.name).length - String(a.name).length);
  return sorted.find((a) => a.name && new RegExp(`(^|\\s)@${String(a.name).toLowerCase().replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}(?=$|[\\s.,!?;:])`).test(t)) || null;
}

export default function useAgentSession(spaceId) {
  const [session, setSession] = useState(() => readSession(spaceId));
  useEffect(() => { setSession(readSession(spaceId)); }, [spaceId]);

  const start = useCallback((agent) => {
    const v = { agentId: String(agent.agentId), name: agent.name, access: agent.access, payer: agent.payer || 'owner', owner: agent.owner || '', ownerUserId: agent.ownerUserId ? String(agent.ownerUserId) : null, at: Date.now() };
    write(spaceId, v); setSession(v);
  }, [spaceId]);
  const clear = useCallback(() => { write(spaceId, null); setSession(null); }, [spaceId]);
  const touch = useCallback(() => setSession((prev) => { if (!prev) return prev; const v = { ...prev, at: Date.now() }; write(spaceId, v); return v; }), [spaceId]);

  // A button elsewhere in the thread (a card, a chip) asks to start one.
  useEffect(() => {
    const onTalk = (e) => { if (e.detail && String(e.detail.spaceId) === String(spaceId) && e.detail.agent) start(e.detail.agent); };
    window.addEventListener('lyra:talk-to', onTalk);
    return () => window.removeEventListener('lyra:talk-to', onTalk);
  }, [spaceId, start]);

  // Idle for too long: it ends by itself.
  useEffect(() => {
    const id = setInterval(() => { if (!readSession(spaceId)) setSession((prev) => (prev ? null : prev)); }, 30000);
    return () => clearInterval(id);
  }, [spaceId]);

  /** The mention to attach to an outgoing message: the conversation's agent, unless the text already @mentions someone. */
  const mentionFor = useCallback((text) => (session && !hasTypedMention(text) ? [{ agentId: session.agentId, name: session.name }] : []), [session]);

  return { session, start, clear, touch, mentionFor };
}

/** Ask the feed to start talking to this agent (used by the cards and chips in the thread). */
export function talkTo(spaceId, agent) {
  window.dispatchEvent(new CustomEvent('lyra:talk-to', { detail: { spaceId, agent } }));
  window.dispatchEvent(new Event('lyra:focus-composer'));
}
